import { useEffect } from "react";

// §14.4 Keep Screen Awake: best effort only. The execution screen passes
// `enabled` (ACTIVE and keepScreenAwake); this hook requests
// navigator.wakeLock.request('screen') while the document is visible,
// re-requests on visibilitychange → visible, and releases on disable/unmount.
// Nothing is persisted, dispatched or synced, and every failure is silent:
// no workout behaviour depends on the lock.

function releaseQuietly(sentinel) {
  if (!sentinel || sentinel.released) return;
  try {
    Promise.resolve(sentinel.release()).catch(() => {});
  } catch (_error) {
    // A release failure never reaches the workout.
  }
}

export default function useScreenWakeLock(enabled, {
  navigatorObject = typeof navigator === "undefined" ? null : navigator,
  documentObject = typeof document === "undefined" ? null : document,
} = {}) {
  useEffect(() => {
    const wakeLock = navigatorObject?.wakeLock;
    if (!enabled || typeof wakeLock?.request !== "function" || !documentObject) return undefined;

    // One effect run owns at most one pending request and one sentinel. A
    // request that resolves after this run ended (disabled, unmounted, a
    // newer run) or while the page is hidden is released, never installed.
    let current = true;
    let pending = false;
    let sentinel = null;
    const visible = () => documentObject.visibilityState === "visible";

    // The browser may release the lock (for example on hide). Forget it and
    // wait for the next visibilitychange → visible: no immediate re-request,
    // so a spontaneous release can never loop.
    function forget(event) {
      if (event?.target && event.target !== sentinel) return;
      sentinel?.removeEventListener?.("release", forget);
      sentinel = null;
    }

    function acquire() {
      if (!current || pending || (sentinel && !sentinel.released) || !visible()) return;
      pending = true;
      let request;
      try {
        request = wakeLock.request("screen");
      } catch (_error) {
        pending = false;
        return;
      }
      Promise.resolve(request).then((lock) => {
        pending = false;
        if (!current || !visible() || !lock) {
          releaseQuietly(lock);
          return;
        }
        sentinel = lock;
        lock.addEventListener?.("release", forget);
      }, () => {
        // Denied/unsupported/aborted: stay silent and do not retry until the
        // next lifecycle opportunity (becoming visible again).
        pending = false;
      });
    }

    function handleVisibility() {
      if (visible()) {
        acquire();
        return;
      }
      // Hidden: the browser releases the lock; treat it as lost.
      const lost = sentinel;
      forget();
      releaseQuietly(lost);
    }

    acquire();
    documentObject.addEventListener("visibilitychange", handleVisibility);
    return () => {
      current = false;
      documentObject.removeEventListener("visibilitychange", handleVisibility);
      const held = sentinel;
      forget();
      releaseQuietly(held);
    };
  }, [documentObject, enabled, navigatorObject]);
}
